import {
  type PatientStaffApiOutContract,
  type VisitPatientPreferenceStaffApiOutContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { useSelector } from 'store/hooks/useSelector'
import { selectLiveVisitHdMockEnabled } from 'store/flags/FlagsSelectors'
import { isDeployedToProduction } from 'utils/isDeployedToProduction'
import {
  type RequestedPharmacy,
  requestedPharmacyFromPatient,
  requestedPharmacyFromVisitPreference,
} from './requestedPharmacy'
import { MOCK_LIVE_VISIT_PATIENT_PREFERENCE } from '../mocks/prj578LiveVisitMocks'

/**
 * PRJ-578: the pharmacy the patient asked for, or `null` where this visit shows none at all.
 *
 * - Async visits read their real `PatientPreference`.
 * - Live visits (video and phone call) read the visit's `PatientPreference` when it has one, else
 *   the patient's own Home Delivery answer (`HomeDeliveryInterest`) — production included, since
 *   both are real data. Where a video visit has no preference, the made-up Home Delivery preference
 *   stands in while the `live_visit_hd_mock` Dev Tools pin is on, never where production is served.
 * - In-person visits are `null`: they have no Orders card.
 *
 * TODO(PRJ-578): drop the mock once the backend populates `PatientPreference` for live visits
 * (most-recent-indicator precedence).
 */
export const useRequestedPharmacy = (
  visitMethod: string | undefined,
  preference: VisitPatientPreferenceStaffApiOutContract | null | undefined,
  patient: PatientStaffApiOutContract | undefined,
): RequestedPharmacy | null => {
  const isLiveVisitMockEnabled = useSelector(selectLiveVisitHdMockEnabled)

  if (visitMethod === 'asynchronous') return requestedPharmacyFromVisitPreference(preference)

  if (visitMethod === 'video' || visitMethod === 'call') {
    if (preference) return requestedPharmacyFromVisitPreference(preference)
    // Video only, like every other consumer of the pin, so a call never gets half a mocked visit.
    if (visitMethod === 'video' && isLiveVisitMockEnabled && !isDeployedToProduction)
      return requestedPharmacyFromVisitPreference(MOCK_LIVE_VISIT_PATIENT_PREFERENCE)
    return requestedPharmacyFromPatient(patient)
  }

  return null
}
